import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";

import { Droppable } from "react-beautiful-dnd";

import { makeStyles } from "@mui/styles";

import FolderListItem from "./FolderListItem";
import PDFListItem from "./PDFListItem";
import VideoListItem from "./VideoListItem";
import ImageListItem from "./ImageListItem";
import AudioListItem from "./AudioListItem";
import MiscFileListItem from "./MiscFileListItem";
import LinkListItem from "./LinkListItem";
import AddIcon from "@mui/icons-material/Add";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";

// Bootstrap

// My Files
import { UserContext, DatabaseContext } from "../../../../context";
import firebase from "../../../../firebase";
import { jsonFromArray, makeid } from "../../../../utility";

import UploadFilesPage from "../../uploading/UploadFilesPage";
import DialogWithTextInput from "../../../DialogWithTextInput";
import { FinderAddMenu } from "./FinderActionMenus";

const useStyles = makeStyles((theme) => ({
    column: {
        position: "relative",
        height: "100%",
        minHeight: 0,
        paddingBottom: 8,
        boxSizing: "border-box",
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehaviorY: "contain",
        scrollbarGutter: "stable",
        scrollbarWidth: "thin",
        scrollbarColor:
            theme.palette.mode === "dark"
                ? "#526158 transparent"
                : "#b8c2ba transparent",
        color: theme.palette.text.primary,
        backgroundColor: theme.palette.background.paper,
        borderRight: `1px solid ${theme.palette.divider}`,
        transition:
            "background-color 150ms ease, box-shadow 150ms ease, color 150ms ease",
        "&::-webkit-scrollbar": {
            width: 8,
        },
        "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor:
                theme.palette.mode === "dark" ? "#526158" : "#c4ccc6",
            border: `2px solid ${theme.palette.background.paper}`,
            borderRadius: 999,
        },
        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor:
                theme.palette.mode === "dark" ? "#68796e" : "#aeb9b1",
        },
    },
    columnDragOver: {
        backgroundColor:
            theme.palette.mode === "dark" ? "#183323" : "#f1f8f3",
        boxShadow: "inset 0 0 0 2px rgba(0, 149, 61, 0.7)",
        animation: "$dropTargetPulse 760ms ease-in-out infinite alternate",
        "& $titleRowPadding": {
            backgroundColor:
                theme.palette.mode === "dark"
                    ? "rgba(24, 51, 35, 0.96)"
                    : "rgba(241, 248, 243, 0.96)",
        },
        "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
        },
    },
    titleRowPadding: {
        margin: 0,
        minHeight: 58,
        padding: "8px 10px",
        display: "flex",
        flexWrap: "nowrap",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        position: "sticky",
        top: 0,
        zIndex: 3,
        backgroundColor:
            theme.palette.mode === "dark"
                ? "rgba(25, 32, 28, 0.96)"
                : "rgba(255, 255, 255, 0.96)",
        borderBottom: `1px solid ${theme.palette.divider}`,
        boxShadow:
            theme.palette.mode === "dark"
                ? "0 2px 8px rgba(0, 0, 0, 0.2)"
                : "0 2px 8px rgba(20, 35, 25, 0.04)",
        backdropFilter: "blur(8px)",
    },
    titleBlock: {
        minWidth: 0,
        flex: 1,
    },
    columnTitle: {
        display: "block",
        margin: 0,
        color: theme.palette.text.primary,
        textAlign: "left",
        fontFamily: "DraigRegular",
        fontSize: 18,
        lineHeight: 1.15,
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
    },
    columnMeta: {
        display: "block",
        marginTop: 3,
        color: theme.palette.text.secondary,
        fontSize: 12,
        lineHeight: 1,
    },
    addButton: {
        flexShrink: 0,
        color: theme.palette.primary.main,
        backgroundColor:
            theme.palette.mode === "dark"
                ? "rgba(31, 184, 94, 0.12)"
                : "rgba(0, 149, 61, 0.09)",
        border:
            theme.palette.mode === "dark"
                ? "1px solid rgba(31, 184, 94, 0.22)"
                : "1px solid rgba(0, 149, 61, 0.15)",
        "&:hover": {
            backgroundColor:
                theme.palette.mode === "dark"
                    ? "rgba(31, 184, 94, 0.2)"
                    : "rgba(0, 149, 61, 0.16)",
            transform: "rotate(4deg) scale(1.04)",
        },
        "&:active": {
            transform: "scale(0.94)",
        },
        "&:focus-visible": {
            outline: "3px solid rgba(0, 149, 61, 0.28)",
            outlineOffset: 1,
        },
        transition:
            "background-color 150ms ease, transform 150ms cubic-bezier(0.2, 0, 0, 1)",
        "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
            "&:hover, &:active": {
                transform: "none",
            },
        },
    },
    "@keyframes dropTargetPulse": {
        from: {
            boxShadow: "inset 0 0 0 2px rgba(0, 149, 61, 0.48)",
        },
        to: {
            boxShadow:
                "inset 0 0 0 2px rgba(0, 149, 61, 0.82), inset 0 0 24px rgba(0, 149, 61, 0.06)",
        },
    },
}));

export default function FinderColumn(props) {
    const classes = useStyles();
    const history = useHistory();
    // Just used to show visually which is selected
    // const [selectedFolderIndex, setSelectedFolderIndex] = useState();
    // const { currentUserData } = useContext(UserContext);
    // const { files, folders } = useContext(DatabaseContext);

    const [clickLocation, setClickLocation] = useState({
        mouseX: null,
        mouseY: null,
    });

    const [folderCount, setFolderCount] = useState(0);
    const [videoCount, setVideoCount] = useState(0);
    const [pdfCount, setPDFCount] = useState(0);
    const [imageCount, setImageCount] = useState(0);
    const [audioCount, setAudioCount] = useState(0);

    const { currentUserData } = useContext(UserContext);
    const { files, folders } = useContext(DatabaseContext);

    const [userCanUpload, setUserCanUpload] = useState(false);

    const [newFolderNameDialogOpen, setNewFolderNameDialogOpen] =
        useState(false);

    const [editingEnabled, setEditingEnabled] = useState(true); // Only false when we are embedding this is a part of the app just used to select files (e.g. in the projects section)

    const [uploadPageIsOpen, setUploadPageIsOpen] = useState(false);

    useEffect(() => {
        // Set the counts of each type so they get the correct index
        setFolderCount(props.column.folders.length);

        let videoCount_ = 0;
        let pdfCount_ = 0;
        let imageCount_ = 0;
        let audioCount_ = 0;

        if (props.column.files) {
            props.column.files.forEach((file) => {
                if (file.type.includes("video")) {
                    videoCount_++;
                } else if (file.type.includes("audio")) {
                    audioCount_++;
                } else if (file.type.includes("image")) {
                    imageCount_++;
                } else if (file.type === "application/pdf") {
                    pdfCount_++;
                }
            });

            setVideoCount(videoCount_);
            setAudioCount(audioCount_);
            setImageCount(imageCount_);
            setPDFCount(pdfCount_);
        }

        if (props.column.folder && props.column.folder.id !== "root") {
            // If User is a manager of the folder, or a System Digital Manager, they can upload files here
            if (props.column.folder && props.column.folder.managers) {
                if (
                    props.column.folder.managers.includes(
                        currentUserData.key,
                    ) ||
                    currentUserData.role === "System Digital Manager"
                ) {
                    setUserCanUpload(true);
                }
            }

            if (
                currentUserData &&
                currentUserData.permissions &&
                props.column.folder.users
            ) {
                // If user has the uploadEditOwnVideo permissions and they are a user of this folder or one of it's ancestors, they can upload files here
                if (currentUserData.permissions.uploadEditOwnVideo) {
                    if (
                        props.column.folder.users.includes(currentUserData.key)
                    ) {
                        setUserCanUpload(true);
                    } else {
                        let parentFolder = folders.find(
                            (f) => f.key === props.column.folder.parent,
                        );
                        if (
                            parentFolder &&
                            parentFolder.users.includes(currentUserData.key)
                        ) {
                            setUserCanUpload(true);
                        }

                        props.column.folder.ancestors.forEach((ancestorKey) => {
                            let ancestorFolder = folders.find(
                                (f) => f.key === ancestorKey,
                            );
                            if (
                                ancestorFolder &&
                                ancestorFolder.users.includes(
                                    currentUserData.key,
                                )
                            ) {
                                setUserCanUpload(true);
                            }
                        });
                    }
                }
            }
        }

        if (props.column.id === "faves_fggh2973jgbvbiu") {
            setUserCanUpload(false);
        }

        if (props.embedded === true) {
            setEditingEnabled(false);
            setUserCanUpload(false);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props, currentUserData]);

    function handleFolderClick(folderIndex) {
        props.column.selectedFolderIndex = folderIndex;
        props.handleFolderSelect(props.index, folderIndex);

        // console.clear();
        // console.log(props.column.folders[folderIndex].createdBy);
        // console.log(props.column.folders[folderIndex].users);
        // console.log("Folder: " + props.column.folders[folderIndex].name);
        // console.log("Parent: " + props.column.folders[folderIndex].parent);
        // console.log("Ancestors:");
        // console.log(props.column.folders[folderIndex].ancestors);
    }

    function deleteFolder(folder) {
        let shouldDelete = true;

        folders.forEach((f) => {
            if (f.parent === folder.key) {
                shouldDelete = false;
            }
        });

        files.forEach((f) => {
            if (f.folder === folder.key) {
                shouldDelete = false;
            }
        });

        if (shouldDelete) {
            let folderRef = firebase
                .database()
                .ref("folders")
                .child(folder.key);

            let selectedFolder =
                props.column.folders[props.column.selectedFolderIndex];

            if (selectedFolder && selectedFolder.key === folder.key) {
                // If deleting the selected folders, select the parent in the UI
                history.push(`/files/${selectedFolder.parent}`);
            }

            folderRef.remove();

            props.showStatusMessage("Folder deleted successfully", "success");
        } else {
            props.showStatusMessage(
                "Can't delete folders that contain subfolders or files",
                "error",
            );
        }
    }

    function handleAddButtonPress(event) {
        setClickLocation({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 2,
        });
    }

    const handleAddMenuAction = (action) => {
        if (action === "newfolder") {
            setNewFolderNameDialogOpen(true);
        } else if (action === "newfile") {
            setUploadPageIsOpen(true);
        } else if (action === "newlink") {
            let currentFolder = props.column.folder;

            if (currentFolder) {
                history.push({
                    pathname: "/addlink/",
                    folder: currentFolder,
                });
            }
        }

        setClickLocation({
            mouseX: null,
            mouseY: null,
        });
    };

    const handleNewFolderNameDialogClose = (folderName, confirmed) => {
        if (confirmed) {
            const trimmedFolderName = folderName.trim();

            if (trimmedFolderName !== "") {
                let currentFolder = props.column.folder;
                if (currentFolder) {
                    // let usersObject = jsonFromArray(currentFolder.users);
                    let managersObject = jsonFromArray(currentFolder.managers);
                    let ancestorsObject = jsonFromArray(
                        currentFolder.ancestors,
                    );
                    ancestorsObject[currentFolder.id] = true;

                    // Add the new folder to firebase

                    var createdBy = "";
                    if (currentUserData) {
                        createdBy = currentUserData.key;
                    }

                    var ref = firebase.database().ref("folders");

                    ref.child(makeid(18)).set({
                        name: trimmedFolderName,
                        parent: currentFolder.id,
                        createdBy: createdBy,
                        // users: usersObject,
                        managers: managersObject,
                        ancestors: ancestorsObject,
                    });
                }

                setNewFolderNameDialogOpen(false);
            }
        } else {
            setNewFolderNameDialogOpen(false);
        }
    };

    function handleCloseUploadPage() {
        setUploadPageIsOpen(false);
    }

    const itemCount =
        (props.column.folders?.length || 0) +
        (props.column.files?.length || 0);

    return (
        <Droppable droppableId={props.column.id}>
            {(provided, snapshot) => (
                <div
                    className={`${classes.column} ${
                        snapshot.isDraggingOver
                            ? classes.columnDragOver
                            : ""
                    }`}
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                >
                    {props.column.name && (
                        <div className={classes.titleRowPadding}>
                            <div className={classes.titleBlock}>
                                <span
                                    className={classes.columnTitle}
                                    title={props.column.name}
                                >
                                    {props.column.name}
                                </span>
                                <span className={classes.columnMeta}>
                                    {itemCount}{" "}
                                    {itemCount === 1 ? "item" : "items"}
                                </span>
                            </div>
                            {userCanUpload && (
                                <Tooltip
                                    title={`Add to ${props.column.name}`}
                                    arrow
                                >
                                    <IconButton
                                        className={classes.addButton}
                                        size="small"
                                        aria-label={`Add to ${props.column.name}`}
                                        onClick={handleAddButtonPress}
                                    >
                                        <AddIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                    )}
                    {props.column.folders.map((folder, index) => {
                        const isSelectedFolder =
                            props.column.selectedFolderIndex === index;
                        const selectionState = !isSelectedFolder
                            ? "none"
                            : props.selectedColumnIndex === props.index
                              ? "current"
                              : props.selectedColumnIndex > props.index
                                ? "ancestor"
                                : "none";

                        return (
                            <FolderListItem
                                key={folder.key}
                                embedded={props.embedded}
                                folder={folder}
                                index={index}
                                handleClick={handleFolderClick}
                                deleteFolder={deleteFolder}
                                showStatusMessage={props.showStatusMessage}
                                selectionState={selectionState}
                            />
                        );
                    })}
                    {props.column.files &&
                        props.column.files
                            .filter((file) => file.type.includes("video"))
                            .sort((a, b) => {
                                if (a.displayName > b.displayName) return 1;
                                if (a.displayName < b.displayName) return -1;
                                return 0;
                            })
                            .map((file, index) => (
                                <VideoListItem
                                    key={file.key}
                                    embedded={props.embedded}
                                    handleFileSelect={props.handleFileSelect}
                                    file={file}
                                    folder={props.column.folder}
                                    index={index + folderCount}
                                    showStatusMessage={props.showStatusMessage}
                                />
                            ))}
                    {props.column.files &&
                        props.column.files
                            .filter((file) => file.type === "application/pdf")
                            .sort((a, b) => {
                                if (a.displayName > b.displayName) return 1;
                                if (a.displayName < b.displayName) return -1;
                                return 0;
                            })
                            .map((file, index) => (
                                <PDFListItem
                                    key={file.key}
                                    embedded={props.embedded}
                                    handleFileSelect={props.handleFileSelect}
                                    file={file}
                                    folder={props.column.folder}
                                    index={index + folderCount + videoCount}
                                    showStatusMessage={props.showStatusMessage}
                                />
                            ))}
                    {props.column.files &&
                        props.column.files
                            .filter((file) => file.type.includes("image"))
                            .sort((a, b) => {
                                if (a.displayName > b.displayName) return 1;
                                if (a.displayName < b.displayName) return -1;
                                return 0;
                            })
                            .map((file, index) => (
                                <ImageListItem
                                    key={file.key}
                                    embedded={props.embedded}
                                    handleFileSelect={props.handleFileSelect}
                                    file={file}
                                    folder={props.column.folder}
                                    index={
                                        index +
                                        folderCount +
                                        videoCount +
                                        pdfCount
                                    }
                                    showStatusMessage={props.showStatusMessage}
                                />
                            ))}
                    {props.column.files &&
                        props.column.files
                            .filter((file) => file.type.includes("audio"))
                            .sort((a, b) => {
                                if (a.displayName > b.displayName) return 1;
                                if (a.displayName < b.displayName) return -1;
                                return 0;
                            })
                            .map((file, index) => (
                                <AudioListItem
                                    key={file.key}
                                    embedded={props.embedded}
                                    handleFileSelect={props.handleFileSelect}
                                    file={file}
                                    folder={props.column.folder}
                                    index={
                                        index +
                                        folderCount +
                                        videoCount +
                                        pdfCount +
                                        imageCount
                                    }
                                    showStatusMessage={props.showStatusMessage}
                                />
                            ))}
                    {editingEnabled &&
                        props.column.files &&
                        props.column.files
                            .filter((file) => file.type.includes("link"))
                            .sort((a, b) => {
                                if (a.displayName > b.displayName) return 1;
                                if (a.displayName < b.displayName) return -1;
                                return 0;
                            })
                            .map((file, index) => (
                                <LinkListItem
                                    key={file.key}
                                    file={file}
                                    folder={props.column.folder}
                                    index={
                                        index +
                                        folderCount +
                                        videoCount +
                                        pdfCount +
                                        imageCount
                                    }
                                    showStatusMessage={props.showStatusMessage}
                                />
                            ))}

                    {props.column.files &&
                        props.column.files
                            .filter(
                                (file) =>
                                    !file.type.includes("video") &&
                                    !file.type.includes("audio") &&
                                    !file.type.includes("image") &&
                                    !file.type.includes("link") &&
                                    file.type !== "application/pdf",
                            )
                            .sort((a, b) => {
                                if (a.displayName > b.displayName) return 1;
                                if (a.displayName < b.displayName) return -1;
                                return 0;
                            })
                            .map((file, index) => (
                                <MiscFileListItem
                                    key={file.key}
                                    embedded={props.embedded}
                                    handleFileSelect={props.handleFileSelect}
                                    file={file}
                                    folder={props.column.folder}
                                    index={
                                        index +
                                        folderCount +
                                        videoCount +
                                        pdfCount +
                                        imageCount +
                                        audioCount
                                    }
                                    showStatusMessage={props.showStatusMessage}
                                />
                            ))}

                    {provided.placeholder}
                    {editingEnabled && (
                        <FinderAddMenu
                            open={clickLocation.mouseY !== null}
                            position={{
                                top: clickLocation.mouseY,
                                left: clickLocation.mouseX,
                            }}
                            onClose={() => handleAddMenuAction()}
                            onAction={handleAddMenuAction}
                        />
                    )}
                    {editingEnabled && (
                        <DialogWithTextInput
                            open={newFolderNameDialogOpen}
                            onClose={handleNewFolderNameDialogClose}
                            title="New Folder"
                            message={`Create a folder in ${props.column.name}.`}
                            confirmLabel="Create"
                            modern
                        />
                    )}
                    {editingEnabled && uploadPageIsOpen && (
                        <UploadFilesPage
                            open={uploadPageIsOpen}
                            onClose={handleCloseUploadPage}
                            folder={props.column.folder}
                        />
                    )}
                </div>
            )}
        </Droppable>
    );
}
